import {
  IQBoostViewStore,
  IqBoostPrompt,
  IqBoostPromptLookupResult,
  PromptMetaExtended,
  ViewType,
  LanguageHelper,
  PIIContactRecord,
} from '@maximizer/ai/data-access';
import {
  EntryType,
  Octopus,
  OpenAI,
  Session,
} from '@maximizer/core/shared/domain';
import { FormControl } from '@angular/forms';
import {
  parseISO,
  formatISO,
  addMinutes,
  roundToNearestMinutes,
} from 'date-fns';
import { hyphenatedToSentence } from '@maximizer/shared/util';
import { MenuOptionName } from '@maximizer/core/shared/ui/enums';

export const IQBoostTextVersion = {
  defaultVersion: 'defaultVersion',
  faVersion: 'faVersion',
} as const;

/** Category stamped on AI-generated CRM timeline records. */
export const AI_GENERATED_CATEGORY = 'AI-Generated';

export enum IQBoostInternalCommand {
  SetEnabled = 'setEnabled',
  SetAnonymized = 'setAnonymized',
}
export interface IQBoostArgs {
  key: string;
  type: EntryType;
  autoOpen?: boolean;
  fromKey?: string;
  fromType?: EntryType;
}

export interface MenuItem {
  collapsed: boolean;
}

export type VariationMatch = {
  variations: string[];
  result: {
    action: MenuOptionName;
    icon: string;
    actionTranslationKey: string;
    typeTranslationKey: string;
  };
};

export type StringOrUndefined = string | undefined;

export class Helper {
  private static readonly ALPHANUMERIC_PATTERN = /[\p{L}\p{N}]/u;
  private static readonly LETTER_PATTERN = /\p{L}/u;
  private static readonly MIN_MEANINGFUL_WORDS = 2;

  static readonly CRM_ACTIONS: VariationMatch[] = [
    {
      variations: ['Write an email', 'Write email', 'Email'],
      result: {
        action: MenuOptionName.WriteEmail,
        icon: 'envelope',
        actionTranslationKey: 'actions.writeEmail',
        typeTranslationKey: 'types.email',
      },
    },
    {
      variations: [
        'Schedule a meeting',
        'Schedule meeting',
        'Meeting',
        'Appointment',
      ],
      result: {
        action: MenuOptionName.ScheduleMeeting,
        icon: 'calendar',
        actionTranslationKey: 'actions.scheduleMeeting',
        typeTranslationKey: 'types.meeting',
      },
    },
    {
      variations: [
        'Schedule a to-do',
        'Schedule to do',
        'Schedule to-do',
        'To-do',
        'Task',
      ],
      result: {
        action: MenuOptionName.ScheduleToDo,
        icon: 'circle-check',
        actionTranslationKey: 'actions.scheduleToDo',
        typeTranslationKey: 'types.toDo',
      },
    },
    {
      variations: ['Add a note', 'Add note', 'Note'],
      result: {
        action: MenuOptionName.AddNote,
        icon: 'note-sticky',
        actionTranslationKey: 'actions.addNote',
        typeTranslationKey: 'types.note',
      },
    },
    {
      variations: ['Interaction'],
      result: {
        action: MenuOptionName.LogInteraction,
        icon: '',
        actionTranslationKey: 'actions.logInteraction',
        typeTranslationKey: 'types.interaction',
      },
    },
    {
      variations: ['document', 'file'],
      result: {
        action: MenuOptionName.AddDocument,
        icon: '',
        actionTranslationKey: 'types.document',
        typeTranslationKey: 'types.document',
      },
    },
    {
      variations: ['addressBook', 'contact', 'client', 'account'],
      result: {
        action: MenuOptionName.CreateOpportunity,
        icon: '',
        actionTranslationKey: 'types.addressBook',
        typeTranslationKey: 'types.addressBook',
      },
    },
  ];

  static normalizeQuestionText(text?: StringOrUndefined): StringOrUndefined {
    const trimmed = text?.trim();

    if (
      !trimmed ||
      !Helper.ALPHANUMERIC_PATTERN.test(trimmed) ||
      !Helper.LETTER_PATTERN.test(trimmed) ||
      !Helper.hasMinimumMeaningfulWords(trimmed)
    ) {
      return undefined;
    }
    return trimmed;
  }

  private static hasMinimumMeaningfulWords(value: string): boolean {
    const meaningfulWordPattern = /\p{L}{3,}/gu;
    const matches = value.match(meaningfulWordPattern);
    return (matches?.length ?? 0) >= Helper.MIN_MEANINGFUL_WORDS;
  }

  static updateAndMarkInputControl(control: FormControl<string>): void {
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  static resetInputControl(control: FormControl<string>): void {
    control.setValue('', { emitEvent: false });
    control.markAsPristine();
    control.markAsUntouched();
  }

  static extractAndDedupeNames<T>(
    addressBook: Octopus.Entity[] | undefined,
    propertyName: Octopus.AbEntryFields,
    extractValue: (nameField: T) => string = (nameField: T) =>
      (nameField as Octopus.ValueAndDisplay<string>)?.Value,
  ): string[] {
    return Helper.extractFieldValuesFrom(addressBook, [propertyName], {
      [propertyName]: (fieldValue: unknown) => extractValue(fieldValue as T),
    })[propertyName];
  }

  static extractFieldValuesFrom<TField extends string>(
    entities: Octopus.Entity[] | undefined,
    fieldNames: readonly TField[],
    extractors: Partial<Record<TField, (fieldValue: unknown) => string>> = {},
  ): Record<TField, string[]> {
    const valuesByField = {} as Record<TField, string[]>;
    const dedupedValuesByField = {} as Record<TField, Set<string>>;

    for (const fieldName of fieldNames) {
      valuesByField[fieldName] = [];
      dedupedValuesByField[fieldName] = new Set<string>();
    }

    if (!entities?.length) {
      return valuesByField;
    }

    for (const entity of entities) {
      for (const fieldName of fieldNames) {
        const extractValue =
          extractors[fieldName] ?? Helper.extractDefaultFieldValue;
        const value = extractValue(entity[fieldName]);

        if (!value?.trim() || dedupedValuesByField[fieldName].has(value)) {
          continue;
        }

        dedupedValuesByField[fieldName].add(value);
        valuesByField[fieldName].push(value);
      }
    }

    return valuesByField;
  }

  /** One partial field map per entity; unlike extractFieldValuesFrom, not deduped across entities. */
  static extractFieldValuesPerEntity<TField extends string>(
    entities: Octopus.Entity[] | undefined,
    fieldNames: readonly TField[],
    extractors: Partial<Record<TField, (fieldValue: unknown) => string>> = {},
  ): Array<Partial<Record<TField, string>>> {
    if (!entities?.length) {
      return [];
    }
    const rows: Array<Partial<Record<TField, string>>> = [];
    for (const entity of entities) {
      const row: Partial<Record<TField, string>> = {};
      for (const fieldName of fieldNames) {
        const extractValue =
          extractors[fieldName] ?? Helper.extractDefaultFieldValue;
        const raw = extractValue(entity[fieldName]);
        const value = raw?.trim();
        if (value) {
          row[fieldName] = value;
        }
      }
      rows.push(row);
    }
    return rows;
  }

  private static readonly addressBookPiiExtractFields = [
    Octopus.AbEntryFields.FirstName,
    Octopus.AbEntryFields.LastName,
    Octopus.AbEntryFields.FullName,
    Octopus.AbEntryFields.Address,
    Octopus.AbEntryFields.Email,
    Octopus.AbEntryFields.Phone,
  ] as const;

  static buildPIIContactRecords(
    entities: Octopus.Entity[] | undefined,
    sessionInfo?: Session,
  ): PIIContactRecord[] {
    const rows = Helper.extractFieldValuesPerEntity(
      entities,
      Helper.addressBookPiiExtractFields,
    );
    const contacts = rows.map((row) => ({
      firstName: row[Octopus.AbEntryFields.FirstName],
      lastName: row[Octopus.AbEntryFields.LastName],
      fullName: row[Octopus.AbEntryFields.FullName],
      address: row[Octopus.AbEntryFields.Address],
      email: row[Octopus.AbEntryFields.Email],
      phone: row[Octopus.AbEntryFields.Phone],
    }));
    contacts.push({
      firstName: sessionInfo?.user?.firstName,
      lastName: sessionInfo?.user?.lastName,
      fullName: sessionInfo?.user?.displayName,
      address:
        [
          sessionInfo?.user?.address?.addressLine1,
          sessionInfo?.user?.address?.addressLine2,
        ]
          .filter(Boolean)
          .join(', ') || undefined,
      email: sessionInfo?.user?.email1?.email,
      phone: sessionInfo?.user?.phone1?.phoneNumber,
    });

    return contacts;
  }

  private static extractDefaultFieldValue(fieldValue: unknown): string {
    if (typeof fieldValue === 'string') {
      return fieldValue;
    } else if (!fieldValue || typeof fieldValue !== 'object') {
      return '';
    }

    return (
      Helper.readNestedStringValue(fieldValue, 'Value') ||
      Helper.readNestedStringValue(fieldValue, 'DisplayValue')
    );
  }

  private static readNestedStringValue(
    fieldValue: object,
    propertyName: 'Value' | 'DisplayValue',
  ): string {
    const propertyValue = (fieldValue as Record<string, unknown>)[propertyName];

    if (typeof propertyValue === 'string') {
      return propertyValue;
    }

    if (propertyValue && typeof propertyValue === 'object') {
      const nestedValue = (propertyValue as Record<string, unknown>)['Value'];
      return typeof nestedValue === 'string' ? nestedValue : '';
    }

    return '';
  }

  static mergeMessages(messages: OpenAI.GPTMessage[]): OpenAI.GPTMessage[] {
    if (!messages.length) {
      return [{ role: 'user', content: '' }];
    }

    const mergedContent = messages
      .map((msg) => msg.content)
      .filter((content) => content && content.trim().length > 0)
      .join('\n\n');

    return [
      {
        role: 'user',
        content: mergedContent,
      },
    ];
  }

  static formatDateRange(date: string): { startDate: string; endDate: string } {
    const parsed = parseISO(date);
    if (!date.includes('T')) {
      const now = new Date();
      parsed.setHours(now.getHours(), now.getMinutes(), now.getSeconds());
    }
    const startDate = roundToNearestMinutes(parsed, {
      nearestTo: 30,
      roundingMethod: 'ceil',
    });
    const endDate = addMinutes(startDate, 30);
    const formatDate = (d: Date) => formatISO(d, { format: 'extended' });

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    };
  }

  static serializeValue(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return '[Complex Object]';
      }
    }

    return String(value);
  }

  static usePrompt(
    store: IQBoostViewStore,
    prompt: IqBoostPrompt,
    useAllHouseholds = false,
  ): void {
    const differentHouseholds = store.useAllHouseholds() !== useAllHouseholds;

    store.setAiResponse('', ViewType.Report);
    store.setViewType(ViewType.Report);
    store.setCurrentPrompt(prompt);

    if (differentHouseholds) {
      // triggerScopeChange cancels before it emits; its handler re-arms loading.
      store.triggerScopeChange({ useAllHouseholds });
    } else {
      // Cancelling runs the previous request's finalize, which clears loading — so raise it after.
      store.cancelInFlight();
      store.setLoading(true);
      store.triggerAskAiEvent();
    }
  }

  /** Finds a CRM action by variation string (case-insensitive). */
  static getCRMActionFromVariation(
    inputVariation: string,
  ): VariationMatch | undefined {
    const matchingAction = Helper.CRM_ACTIONS.find((action) =>
      action.variations.some(
        (variation) => variation.toLowerCase() === inputVariation.toLowerCase(),
      ),
    );
    return matchingAction;
  }

  static getPromptNameFromLookup(
    prompt?: Pick<IqBoostPromptLookupResult, 'name' | 'config'>,
    language = LanguageHelper.language(),
  ): string {
    if (!prompt) return '';

    const title = prompt.config?.maximizer?.title;
    const fallbackTitle = hyphenatedToSentence(prompt.name);

    if (typeof title === 'string') {
      return title;
    }

    if (title?.value && typeof title.value === 'object') {
      return title.value[language] ?? fallbackTitle;
    } else {
      return (title?.value as StringOrUndefined) ?? fallbackTitle;
    }
  }

  // Outlook's email assist still passes the Langfuse-era prompt shape.
  static getPromptName(
    extPromptMeta?: PromptMetaExtended,
    language = LanguageHelper.language(),
  ): string {
    return Helper.getPromptNameFromLookup(
      extPromptMeta && {
        name: extPromptMeta.promptMeta.name,
        config: extPromptMeta.promptMeta.lastConfig,
      },
      language,
    );
  }

  static getLanguageSuggestedPrompt(
    languagePrompt: Pick<IqBoostPromptLookupResult, 'config'> | undefined,
    language = LanguageHelper.language(),
  ): string {
    const suggestedPrompts = languagePrompt?.config?.maximizer?.suggestedPrompts;

    if (!suggestedPrompts) return '';

    const prompt = suggestedPrompts.find(
      (prompt) =>
        (prompt.type as StringOrUndefined)?.toLowerCase() ===
        language.toLowerCase(),
    );
    return prompt?.text ?? '';
  }
}
